import React, { useLayoutEffect, useRef, useState } from "react";
import { useRouteMatch } from "react-router-dom";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Stack, Typography } from "@mui/material";
import { WorkspaceAllFilesIcon, WorkspaceRecentlyAddedIcon } from "assets/material-custom-icons";
import { ArrowTrigger } from "components/old-data-box-components/Arrow-trigger/ArrowTrigger";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { Routes } from "constants/routes";
import { useClientParamsHooks, useNavigate } from "hooks";

import { useClientWorkspaceCtx } from "./context/ClientWorkspace-context";
import { Workspace } from "./Data-box-components/Workspace";
import { AddNewMenu } from "./Menus/AddNewMenu";

const COLLAPSED_WIDTH = 300;
const EXPANDED_WIDTH = 600;

const BOTTOM_SECTION_HEIGHT = 125;

interface NavItemProps {
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
  onClick: () => void;
  hint?: {
    title: string;
    icon: React.ReactElement;
  };
}

const NavItem: React.FC<NavItemProps> = ({ icon, label, isActive, onClick, hint }) => (
  <Stack
    direction="row"
    alignItems="center"
    sx={{
      height: "36px",
      padding: "0 5px",
      borderRadius: "20px",
      cursor: "pointer",
      flexShrink: 0,
      backgroundColor: isActive ? "#DBE8F5" : "transparent",
      "&:hover": { backgroundColor: "#ECF1F8" }
    }}
    gap="5px"
  >
    <Stack sx={{ padding: "16px" }} />
    <Stack
      direction="row"
      alignItems="center"
      gap="5px"
      onClick={onClick}
      sx={{ flex: 1, minWidth: 0, cursor: "pointer" }}
    >
      {icon}
      <Typography sx={{ color: "#20293C", fontWeight: 400, fontSize: "16px" }}>{label}</Typography>
      {hint && (
        <MaterialTooltip isShow title={hint.title} centerText>
          {hint.icon}
        </MaterialTooltip>
      )}
    </Stack>
  </Stack>
);

export const DataBox = () => {
  const navigate = useNavigate();
  const { clientId } = useClientParamsHooks();
  const { currentFolder, refetchFolderMetadata } = useClientWorkspaceCtx();
  const [isExpanded, setIsExpanded] = useState(false);
  const allFilesMatch = useRouteMatch({ path: Routes.clientWorkspaceAllFiles, exact: true });
  const recentlyAddedMatch = useRouteMatch({ path: Routes.clientWorkspaceRecentlyAdded, exact: true });
  const contentRef = useRef<HTMLDivElement>(null);
  const [availableHeightForFolders, setAvailableHeightForFolders] = useState(0);

  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const update = () => setAvailableHeightForFolders(Math.max(0, el.clientHeight - BOTTOM_SECTION_HEIGHT));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <Stack sx={{ position: "relative", flexShrink: 0, width: `${COLLAPSED_WIDTH}px` }}>
      <Stack
        sx={{
          minWidth: `${COLLAPSED_WIDTH}px`,
          width: isExpanded ? `${EXPANDED_WIDTH}px` : `${COLLAPSED_WIDTH}px`,
          height: "100%",
          backgroundColor: "#f6f8fc",
          overflow: "hidden",
          transition: "width 0.3s ease-in-out, box-shadow 0.3s ease-in-out, left 0.3s ease-in-out",
          position: "absolute",
          left: 0,
          top: 0,
          zIndex: 10,
          ...(isExpanded && {
            boxShadow: "4px 0 16px rgba(0,0,0,0.15)"
          })
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ flexShrink: 0, marginBottom: "5px" }}
        >
          <Stack direction="row" alignItems="center" gap="5px">
            <AddNewMenu currentFolder={currentFolder} refetchHandler={refetchFolderMetadata} />
          </Stack>
          <ArrowTrigger
            onClick={() => setIsExpanded((v) => !v)}
            isOpen={isExpanded}
            closeState="right"
            openState="left"
            sx={{ ml: 0.5 }}
          />
        </Stack>
        <Stack
          ref={contentRef}
          sx={{ overflow: "auto", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
        >
          <Stack sx={{ flexShrink: 0, minHeight: 0 }}>
            <Workspace availableHeight={availableHeightForFolders} />
          </Stack>
          <NavItem
            icon={<WorkspaceAllFilesIcon sx={{ fill: "#0A3142", fontSize: 20 }} />}
            label="All Items"
            isActive={!!allFilesMatch}
            onClick={() => navigate({ route: "clientWorkspaceAllFiles", params: { client_id: clientId } })}
          />
          <NavItem
            icon={<WorkspaceRecentlyAddedIcon sx={{ fill: "#0A3142", fontSize: 20 }} />}
            label="Recently Added"
            isActive={!!recentlyAddedMatch}
            onClick={() => navigate({ route: "clientWorkspaceRecentlyAdded", params: { client_id: clientId } })}
            hint={{
              title: "Shows files added within the last 3 months",
              icon: <InfoOutlinedIcon sx={{ fontSize: 15, color: "#0A3142", opacity: 0.7 }} />
            }}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};
